import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";

export function SpelpausInfoPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const title = isSv
    ? "Spelpaus.se — det svenska avstängningsregistret"
    : "Spelpaus.se — the Swedish self-exclusion register";
  const desc = isSv
    ? "Spelpaus.se är det nationella registret för självavstängning från svenskt licensierat spel."
    : "Spelpaus.se is the national register for self-exclusion from Swedish-licensed gambling.";

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: "Spelpaus" }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-8 max-w-3xl [&>h2]:mt-10 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3 [&>ul]:my-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul>li]:my-1">
        <h2>{isSv ? "Vad är Spelpaus?" : "What is Spelpaus?"}</h2>
        <p>
          {isSv
            ? "Spelpaus.se är ett verktyg som låter dig stänga av dig själv från allt licensierat spel i Sverige med ett enda BankID-klick. Tjänsten administreras av Spelinspektionen och lanserades samtidigt som den nya spellagstiftningen 2019. Avstängningen gäller alla operatörer med svensk licens — du behöver inte stänga av dig på varje sajt separat."
            : "Spelpaus.se is a tool that lets you self-exclude from all licensed gambling in Sweden with a single BankID click. The service is administered by Spelinspektionen and launched alongside the new gambling law in 2019. The block applies to every operator with a Swedish license — you do not have to self-exclude on every site separately."}
        </p>

        <h2>{isSv ? "Så registrerar du en avstängning" : "How to register a block"}</h2>
        <p>
          {isSv
            ? "Gå till spelpaus.se, klicka på ”Stäng av dig” och logga in med BankID. Du väljer en period på 1, 3, 6 eller 12 månader. Avstängningen träder i kraft direkt och syns hos alla licensierade bolag inom några sekunder. När den är aktiv kan du inte logga in på licensierade casinosajter, du kan inte ta emot kampanjer eller direktreklam, och nya konton blockeras automatiskt vid registrering."
            : "Go to spelpaus.se, click ”Block yourself” and log in with BankID. Pick a period of 1, 3, 6 or 12 months. The block takes effect immediately and appears with every licensed operator within seconds. While active, you cannot log in to licensed casino sites, you cannot receive campaigns or direct marketing, and new account registrations are automatically blocked."}
        </p>

        <h2>{isSv ? "Avstängningens räckvidd" : "Scope of the block"}</h2>
        <p>
          {isSv
            ? "Spelpaus täcker alla operatörer med svensk spellicens — casino, betting, poker och bingo. Den täcker också direktmarknadsföring: licensierade bolag får inte skicka e-post, SMS eller fysisk reklam till en avstängd person. Spelpaus täcker inte casinon utan svensk licens, lotter köpta i livsmedelsbutik som inte kräver licens eller landbaserade kasinon utomlands."
            : "Spelpaus covers every operator with a Swedish gambling license — casino, betting, poker and bingo. It also covers direct marketing: licensed operators are not allowed to send email, SMS or physical mail to a self-excluded person. Spelpaus does not cover casinos without a Swedish license, supermarket lottery products that do not require a license, or land-based casinos abroad."}
        </p>

        <h2>{isSv ? "När avstängningen tar slut" : "When the block ends"}</h2>
        <p>
          {isSv
            ? "Avstängningen kan inte hävas i förtid. När den löper ut förblir du avstängd tills du själv aktivt loggar in på spelpaus.se och bekräftar att du vill spela igen. Det skapar ett medvetet hinder och hjälper dig fundera över valet."
            : "The block cannot be lifted early. When it expires you remain blocked until you actively log in to spelpaus.se and confirm you want to play again. This creates a deliberate friction point and helps you reconsider the choice."}
        </p>

        <h2>{isSv ? "Stöd utöver Spelpaus" : "Support beyond Spelpaus"}</h2>
        <p>
          {isSv
            ? "Spelpaus är ett verktyg, inte en behandling. Om du eller någon nära dig kämpar med spelandet finns gratis och anonym hjälp från Stödlinjen på 020-81 91 00. Stödlinjen erbjuder telefon, chatt och e-post på stodlinjen.se. Vården kan dessutom hänvisa till specialistmottagningar för spelberoende."
            : "Spelpaus is a tool, not a treatment. If you or someone close to you is struggling with gambling there is free and anonymous help from Stödlinjen at 020-81 91 00. Stödlinjen offers phone, chat and email at stodlinjen.se. Healthcare in Sweden can also refer you to specialist clinics for gambling addiction."}
        </p>

        <h2>{isSv ? "Vanliga frågor" : "Frequently asked questions"}</h2>
        <ul>
          <li>
            <strong>{isSv ? "Påverkar Spelpaus min kreditvärdighet? " : "Does Spelpaus affect my credit score? "}</strong>
            {isSv
              ? "Nej. Inga uppgifter delas med banker eller kreditupplysningsföretag."
              : "No. No data is shared with banks or credit-rating agencies."}
          </li>
          <li>
            <strong>{isSv ? "Kan en arbetsgivare se min avstängning? " : "Can an employer see my block? "}</strong>
            {isSv
              ? "Nej. Spelpaus är inte ett offentligt register. Bara licensierade speloperatörer kontrollerar registret."
              : "No. Spelpaus is not a public register. Only licensed gambling operators query the register."}
          </li>
          <li>
            <strong>{isSv ? "Vad händer med mina pengar på spelkonton? " : "What happens to money in my accounts? "}</strong>
            {isSv
              ? "Pengar och vinster kan tas ut även under en avstängning. Operatören är skyldig att betala ut din behållning."
              : "Money and winnings can be withdrawn during a block. The operator is required to pay out your balance."}
          </li>
        </ul>
      </article>

      <InternalLinks context="responsible" />
    </div>
  );
}
